import { Theme, Typography, useMediaQuery } from '@mui/material';
import { FC, useState } from 'react';

import { CameraCapture } from '@components/CameraCapture';
import Header from '@components/Capture/components/Header';
import { DialogDarkFullscreen } from '@components/DialogDarkFullscreen';

import { ActionComponentProps } from '../../../index';
import { ActionWithInfo } from '../ActionWithInfo';
import ImagePreviewContainer from './ImagePreviewContainer';
import { getButtonConfigs } from './helper';
import { useUploadImage } from './useUploadImage';

type CaptureMode = 'capture' | 'review';

interface ActionProps {
  mailpieces: ActionComponentProps['mailpieces'];
  open: boolean;
  type?: string;
  mode: CaptureMode;
  image: string | null;
  isMobile: boolean;
  skipNotifyRecipient: boolean;
  comment: string;
  notifyMailroom: boolean;
  onClose: () => void;
  onCapture: (imageSrc: string) => void;
  onRetake: () => void;
}

const FinishDeliveryWithImageAction: FC<ActionProps> = ({
  mailpieces,
  open,
  type,
  mode,
  image,
  isMobile,
  skipNotifyRecipient,
  comment,
  notifyMailroom,
  onClose,
  onCapture,
  onRetake,
}) => {
  const { uploadImage, loading } = useUploadImage(mailpieces, onClose, type);

  const handleComplete = () => {
    if (image) {
      void uploadImage(image, skipNotifyRecipient, comment, notifyMailroom);
    }
  };

  const buttons = getButtonConfigs(onRetake, handleComplete, loading);

  return (
    <DialogDarkFullscreen open={open}>
      <Header
        onClose={onClose}
        title={mode === 'capture' ? 'Capture Image' : 'Review'}
        buttons={mode === 'capture' ? [] : buttons}
      />

      {mode === 'capture' && <CameraCapture capture={onCapture} disabled={loading} />}
      {mode === 'review' && image && <ImagePreviewContainer image={image} isMobile={isMobile} buttons={buttons} />}
    </DialogDarkFullscreen>
  );
};

const FinishDeliveryWithImage: FC<ActionComponentProps> = ({ mailpieces, actionStarted, onActionFinish, type }) => {
  const [mode, setMode] = useState<CaptureMode>('capture');
  const [image, setImage] = useState<string | null>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const handleImageCapture = (imageSrc: string) => {
    setImage(imageSrc);
    setMode('review');
  };

  const handleRetake = () => {
    setImage(null);
    setMode('capture');
  };

  return (
    <ActionWithInfo
      open={actionStarted}
      title="Complete with image"
      onClose={onActionFinish}
      acceptButtonTitle="Capture Image"
      infoContent={
        <Typography variant="body1" color="text.secondary">
          {mailpieces.length === 1 ? (
            <>Please capture an image of this item.</>
          ) : (
            <>
              Please capture an image of <strong>{mailpieces.length}</strong> items.
            </>
          )}
        </Typography>
      }
    >
      {({ step, skipNotifyRecipient, comment, notifyMailroom, onClose }) => {
        const handleCloseAction = () => {
          setImage(null);
          setMode('capture');
          onClose();
        };

        return (
          <FinishDeliveryWithImageAction
            mailpieces={mailpieces}
            open={actionStarted && step === 'action'}
            type={type}
            mode={mode}
            image={image}
            isMobile={isMobile}
            skipNotifyRecipient={skipNotifyRecipient}
            comment={comment}
            notifyMailroom={notifyMailroom}
            onClose={handleCloseAction}
            onCapture={handleImageCapture}
            onRetake={handleRetake}
          />
        );
      }}
    </ActionWithInfo>
  );
};

export default FinishDeliveryWithImage;
